import {ActionIcon, Button, Group, TextInput, Title} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconSearch, IconUpload, IconX} from '@tabler/icons-react'
import {useNavigate} from '@tanstack/react-router'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'

import {CreateCustomerModal} from './create-customer-modal'
import {CustomerImportModal} from './customer-import-modal'
import type {CustomerListItem} from './schemas'
import {useCreateCustomer, useCustomers} from './use-customers'

function hasActiveFilters(name: string) {
    return Boolean(name.trim())
}

function compareCustomers(
    left: CustomerListItem,
    right: CustomerListItem,
    columnAccessor: DataTableSortStatus<CustomerListItem>['columnAccessor'],
) {
    if (columnAccessor === 'contactCount') {
        return left.contactCount - right.contactCount
    }

    const leftValue = String(left[columnAccessor as keyof CustomerListItem] ?? '')
    const rightValue = String(right[columnAccessor as keyof CustomerListItem] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortCustomers(
    customers: CustomerListItem[],
    sortStatus: DataTableSortStatus<CustomerListItem>,
) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...customers].sort(
        (left, right) =>
            directionMultiplier * compareCustomers(left, right, sortStatus.columnAccessor),
    )
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

export function CustomersPage() {
    const navigate = useNavigate()
    const user = useCurrentUser()
    const [nameFilter, setNameFilter] = useState('')
    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [importModalOpen, setImportModalOpen] = useState(false)
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<CustomerListItem>>({
        columnAccessor: 'name',
        direction: 'asc',
    })

    const {data: customers = [], isLoading} = useCustomers({name: debouncedName})
    const createCustomer = useCreateCustomer()

    const filtersActive = hasActiveFilters(debouncedName)

    const sortedCustomers = useMemo(
        () => sortCustomers(customers, sortStatus),
        [customers, sortStatus],
    )

    const columns = useMemo((): DataTableColumn<CustomerListItem>[] => {
        return [
            {
                accessor: 'name',
                title: 'Name',
                sortable: true,
                filter: (
                    <TextInput
                        label="Customer name"
                        description="Show customers whose names include the specified text"
                        placeholder="Search customer names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(nameFilter, () => setNameFilter(''))}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: nameFilter !== '',
            },
            {
                accessor: 'contactCount',
                title: 'Contacts',
                sortable: true,
                textAlignment: 'right',
            },
        ]
    }, [nameFilter])

    async function handleCreate(input: Parameters<typeof createCustomer.mutateAsync>[0]) {
        const customer = await createCustomer.mutateAsync(input)
        void navigate({to: '/customers/$customerId', params: {customerId: customer.id}})
    }

    return (
        <>
            <Group justify="space-between" mb="md">
                <Title order={1}>Customers</Title>
                <Group gap="sm">
                    {isAdmin(user) && (
                        <Button
                            variant="default"
                            leftSection={<IconUpload size={16} />}
                            onClick={() => setImportModalOpen(true)}
                        >
                            Import CSV
                        </Button>
                    )}
                    <Button onClick={() => setCreateModalOpen(true)}>Add customer</Button>
                </Group>
            </Group>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                idAccessor="id"
                records={sortedCustomers}
                columns={columns}
                fetching={isLoading}
                minHeight={sortedCustomers.length === 0 ? 180 : undefined}
                noRecordsText={
                    filtersActive ? 'No customers match your filters.' : 'No customers yet.'
                }
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                onRowClick={({record}) =>
                    void navigate({
                        to: '/customers/$customerId',
                        params: {customerId: record.id},
                    })
                }
            />

            <CreateCustomerModal
                opened={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onCreate={handleCreate}
            />

            <CustomerImportModal
                opened={importModalOpen}
                onClose={() => setImportModalOpen(false)}
            />
        </>
    )
}
